import { useEffect, useState } from 'react';
import { Button, Checkbox, Dropdown } from 'semantic-ui-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { FormSchemaAwareWidget } from '~/components/form';
import { COMPANY_SUBTYPE_CODES, COMPANY_SUBTYPE_NAMES, CompanySubtypeCode } from '~/types/company';
import { usePromiseWatcher, useForm } from '~/hooks';
import { fetchRoles, signup } from '~/api/user';
import { ErrorTexts, UserRole } from '~/constants';
import { FormContextProvider } from '~/hooks/providers/FormContextProvider';
import AuthForm from '../components/AuthForm';
import Link from '~/components/Link';
import toast from 'react-hot-toast';
import { ToastDuration } from '~/constants/toasts';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

interface UserSignupProps {
  userType: any;
  inviteToken: any;
  presetEmail: string;
  defaults?: any;
  externalCompanyId?: string;
}

interface UserSignupFormState {
  name: string;
  company: string;
  companyTags: CompanySubtypeCode[];
  email: string;
  password: string;
  passwordConfirm: string;
  roleName: string;
  roleId: string;
}

const UserSignupImpl = ({
  userType,
  inviteToken,
  presetEmail,
  externalCompanyId,
}: UserSignupProps) => {
  const [agreed, setAgreed] = useState(false);
  const [showErrors, setShowErrors] = useState<undefined | true>(undefined);
  const [emailExistsError, setEmailExistsError] = useState('');
  const [emailExistMap, setEmailExistMap] = useState<{ [key: string]: true }>(
    {}
  );
  const { formState, updateFormValue } = useForm<UserSignupFormState>();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const needsCompanySignup =
    !inviteToken ||
    (inviteToken && userType === UserRole.EXTERNAL && !externalCompanyId); // TODO add check here to look for external company role code + null externalCompanyId

  // Set up form validation states
  const requiredFieldErrors = {
    name: !formState.name ? 'Required' : '',
    company: needsCompanySignup && !formState.company ? 'Required' : '',
    email: !formState.email ? 'Required' : '',
    password: !formState.password ? 'Required' : '',
    passwordConfirm: !formState.passwordConfirm ? 'Required' : '',
  };
  const emailError = !emailRegex.test(formState.email) ? 'Invalid email' : '';
  const passwordLengthError =
    formState.password && formState.password.length < 8
      ? 'Password must be at least 8 characters'
      : '';
  const passwordMatchError =
    formState.passwordConfirm &&
    formState.passwordConfirm !== formState.password
      ? 'Passwords do not match'
      : '';

  const valid =
    !Object.values(requiredFieldErrors).some((error) => !!error) &&
    !emailExistsError &&
    !emailError &&
    !passwordLengthError &&
    !passwordMatchError;

  const { execute, status } = usePromiseWatcher(
    async (recaptchaToken: string) => {
      return signup({
        ...formState,
        inviteToken,
        recaptchaToken,
      }).then(() => {
        // Redirect to home page after successful signup.
        // Signup endpoint should also log the user in.
        window.location.href = '/confirm-email';
      });
    },
    {
      messageStub: 'signing you up',
      error: (e: any) => {
        if (e.message === ErrorTexts.USER_ALREADY_EXISTS) {
          setEmailExistMap({ ...emailExistMap, [formState.email]: true });
          setShowErrors(true);
          return (
            <div>
              There is an existing account associated with this e-mail address.{' '}
              Please enter another e-mail, or if you believe this to be in
              error, contact us via email at{' '}
              <a href='mailto:support@conductor.solar'>
                support@conductor.solar
              </a>
            </div>
          );
        } else if (e.message?.includes('Security verification')) {
          // Handle reCAPTCHA-specific errors with helpful messaging
          return (
            <div>
              {e.message} If you continue to experience issues, please contact
              us at{' '}
              <a href='mailto:support@conductor.solar'>
                support@conductor.solar
              </a>
            </div>
          );
        }
      },
    },
    [formState, inviteToken],
    { toastOpts: { id: 'signup', duration: ToastDuration.LONG } } as any
  );

  const handleSignup = async () => {
    if (!valid) {
      toast.error('Please fix your errors and try again.');
      setShowErrors(true);
      return;
    }

    if (!executeRecaptcha) {
      toast.error(
        'Security verification is loading. Please wait a moment and try again.'
      );
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha('signup');
      if (!recaptchaToken) {
        toast.error(
          'Security verification failed to generate. Please refresh the page and try again.'
        );
        return;
      }
      await execute(recaptchaToken);
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      // Check if it's a network error or other specific error
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        toast.error(
          'Network error during security verification. Please check your connection and try again.'
        );
      } else {
        toast.error(
          'Security verification encountered an error. Please refresh the page and try again.'
        );
      }
    }
  };

  if (presetEmail && presetEmail !== formState.email) {
    updateFormValue('email', presetEmail);
  }

  const { execute: getRoles } = usePromiseWatcher(
    async () => {
      const roles = await fetchRoles();
      const role = Object.values(roles).find(
        (role: any) => role.code === userType
      ) as any;
      updateFormValue('roleId', role.id);
    },
    'fetching your role id',
    [userType]
  );
  useEffect(() => {
    if (needsCompanySignup) {
      getRoles();
    }
  }, []);

  useEffect(() => {
    if (emailExistMap[formState.email]) {
      setEmailExistsError('E-mail already in use');
    } else {
      setEmailExistsError('');
    }
  }, [formState.email, emailExistMap]);

  let headerText = 'New User Signup';
  if (userType === UserRole.INSTALLER) {
    headerText += ' — Developer / EPC';
  } else if (userType === UserRole.INVESTOR) {
    headerText += ' — Investor';
  }

  return (
    <AuthForm width='32rem' header={headerText}>
      <FormSchemaAwareWidget
        schemaKey='name'
        fluid
        placeholder='Full name'
        errorText={showErrors && requiredFieldErrors.name}
      />
      {needsCompanySignup && (
        <FormSchemaAwareWidget
          schemaKey='company'
          fluid
          placeholder='Company name'
          errorText={showErrors && requiredFieldErrors.company}
        />
      )}
      {needsCompanySignup && (
        <div style={{ marginBottom: '1.5rem' }}>
          <Dropdown
            id='companyTags'
            fluid
            multiple
            selection
            options={COMPANY_SUBTYPE_CODES.map((code) => ({
              key: code,
              text: COMPANY_SUBTYPE_NAMES[code],
              value: code,
            }))}
            value={formState.companyTags ?? []}
            onChange={(_, { value }) =>
              updateFormValue('companyTags', value as CompanySubtypeCode[])
            }
            placeholder='Company type (select all that apply)'
          />
          {/* <Popup
            inverted
            trigger={<Icon name="info circle" style={{ color: '#005b8e', marginLeft: '4px' }} tabIndex={0} />}
            content={
              <div>
                <strong style={{ fontSize: '16px' }}>Company Type Definitions</strong>
                <p style={{ color: '#FFFFFF', margin: '1em 0em', fontSize: '16px' }}>
                  Here’s how Conductor defines these roles:
                </p>
                <ul style={{ color: '#FFFFFF', margin: '1em 0em', paddingLeft: '1.2em' }}>
                  <li>
                    <strong>EPC:</strong> Engineering, Procurement, and Construction - a company contracting to build the energy system
                  </li>
                  <li>
                    <strong>DEV:</strong> Developer - a company advancing the project from site selection through utility interconnection and permitting
                  </li>
                  <li>
                    <strong>IPP:</strong> Independent Power Producer - a company owning and operating the energy system once it is built
                  </li>
                </ul>
              </div>

            }
            position="top center"
            wide="very"
            on={['hover', 'focus']}
          /> */}
        </div>
      )}
      <FormSchemaAwareWidget
        schemaKey='email'
        fluid
        placeholder='Email'
        autoComplete='username'
        type='email'
        readOnly={!!presetEmail}
        errorText={
          showErrors &&
          (requiredFieldErrors.email || emailError || emailExistsError)
        }
      />
      <FormSchemaAwareWidget
        schemaKey='password'
        fluid
        placeholder='Password'
        autoComplete='new-password'
        type='password'
        errorText={
          showErrors && (requiredFieldErrors.password || passwordLengthError)
        }
      />
      <FormSchemaAwareWidget
        schemaKey='passwordConfirm'
        fluid
        placeholder='Repeat Password'
        autoComplete='new-password'
        type='password'
        errorText={
          showErrors &&
          (requiredFieldErrors.passwordConfirm || passwordMatchError)
        }
      />
      <Checkbox
        onChange={() => setAgreed(!agreed)}
        checked={agreed}
        label={
          <label>
            I agree to the{' '}
            <Link as='a' href='https://conductor.solar/terms-of-service' bold>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link as='a' href='https://conductor.solar/privacy-policy' bold>
              Privacy Policy
            </Link>
            .
          </label>
        }
      />
      <Button
        disabled={!agreed}
        loading={status === 'pending'}
        onClick={handleSignup}
        fluid
        secondary
        size='large'
      >
        Create Account
      </Button>
    </AuthForm>
  );
};

const UserSignup = ({
  userType,
  inviteToken,
  presetEmail,
  externalCompanyId,
}: UserSignupProps) => {
  const defaults: UserSignupFormState = {
    name: '',
    company: '',
    companyTags: [],
    email: presetEmail ?? '',
    password: '',
    passwordConfirm: '',
    roleName: userType,
    roleId: '',
  };

  return (
    <FormContextProvider defaults={defaults}>
      <UserSignupImpl
        {...{ userType, inviteToken, presetEmail, externalCompanyId }}
      />
    </FormContextProvider>
  );
};

export default UserSignup;
